import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BilateralTradeProductType } from '@ebalance/proto/bfftypes/v1/bilateral_trade_product_type_pb'
import { DeliveryCalendarKey } from '@ebalance/proto/bfftypes/v1/delivery_calendar_key_pb'
import { TradingContractStatus } from '@ebalance/proto/bfftypes/v1/trading_contract_status_pb'
import { TradingCustomDayCategory } from '@ebalance/proto/bfftypes/v1/trading_custom_day_category_pb'
import { TradingDeliveryType } from '@ebalance/proto/bfftypes/v1/trading_delivery_type_pb'
import { TradingExecutionRoute } from '@ebalance/proto/bfftypes/v1/trading_execution_route_pb'
import { TradingTimeZoneType } from '@ebalance/proto/bfftypes/v1/trading_time_zone_type_pb'
import { useTranslation } from 'react-i18next'

import { type BadgeProps } from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import type { DeliveryTypeKind } from '~/models/deliveryType'
import {
  convertDeliveryTypeLabel,
  resolveDeliveryTypeKind,
} from '~/models/deliveryType'
import {
  type StatusKind,
  resolveStatusKind,
} from '~/pages/SupplyBilateral/models/recapBadge'

/**
 * SupplyBilateral 取引一覧テーブル用バッジコンポーネント群。
 *
 * プロトタイプ (design/supply/bilateral.html) の視覚装飾に揃える:
 *   - 売買 (buy/sell カラー)
 *   - 約定経路 (enechain / eSquareLive / 手入力)
 *   - 受渡区分 (BG 系 / JEPX 系 / 金融的決済)
 *
 * design HTML は 4 バッジすべてに semantic クラス (`badge-ok` / `badge-info` /
 * `badge-neutral` / `badge-warn` / `badge-error`) を当てており、 `.badge` base の寸法は
 * DS `Badge` (14px / 高さ 24px) と同一のため、 状態表現は DS `Badge` の `status` に統一
 * する (= 旧 `BadgePill` の生の色ペア + 独自寸法 10px は design 違反だったため廃止)。
 */

type BadgeStatus = NonNullable<BadgeProps['status']>

// -----------------------------------------------------------------------------
// StatusBadge — 契約ステータス (design/supply/bilateral.html #bilateral-table
// stBadge 準拠: active=成立済/badge-ok(緑), draft=ドラフト/badge-warn(黄),
// expired=期間満了/badge-neutral(灰), cancelled=取消/badge-error(赤))
// -----------------------------------------------------------------------------

type StatusBadgeProps = {
  status: TradingContractStatus
}

/** kind → ecloud `Badge` の semantic status。 */
const STATUS_BADGE_KIND: Record<StatusKind, BadgeStatus> = {
  active: 'positive',
  draft: 'warning',
  expired: 'neutral',
  cancelled: 'negative',
}

/**
 * ライフサイクル状態バッジ。 ecloud `Badge` の semantic status で色分けする。
 *
 * status → 種別解決は `resolveStatusKind` (RecapModal と共通の SoT) に統一する。
 * これにより UNSPECIFIED / 未知値は default → 'draft' (黄) に落ち、 proto field 未反映の
 * ロールアウト過渡期に Cancelled / Draft 契約が「成立済」(緑) で誤表示される事故を防ぐ。
 */
export const StatusBadge = ({
  status,
}: StatusBadgeProps): React.ReactElement => {
  const { t } = useTranslation()
  const kind = resolveStatusKind(status)
  return (
    <Badge status={STATUS_BADGE_KIND[kind]}>
      {t(`page:supplyBilateral.status.${kind}`)}
    </Badge>
  )
}

// -----------------------------------------------------------------------------
// ExecutionRouteBadge
// -----------------------------------------------------------------------------

type ExecutionRouteBadgeProps = {
  route: TradingExecutionRoute
}

/**
 * 約定経路 → semantic status (design `supply/bilateral.html` の badge クラス準拠)。
 *
 * eSquare Live = `badge-ok` / enechain Broker = `badge-info` / 手入力 = `badge-neutral` /
 * 常時BU = `.trade-bu` (= info)。 写像を持たない値 (UNSPECIFIED / 未知) はバッジを
 * 描画しない。
 */
export const EXECUTION_ROUTE_BADGE_STATUS: Record<
  number,
  BadgeStatus | undefined
> = {
  // 色が付くのは **システム連携の 2 経路だけ** (design `supply/bilateral.html:681-682`)。
  //
  //   eSquare Live = `badge-ok`   → positive
  //   enechain     = `badge-info` → info
  //
  // 残りは design (同 :685-688, :694) がすべて `badge-neutral` にしている。
  // 常時BU / ベースロード市場 / 先渡し市場 を `info` にしていたのは実装だけの拡張で、
  // 「外部市場経由も進行中だから info」という後付けの解釈だった。色が 5 値中 3 値に
  // 広がると **色で経路を見分ける手掛かりが消える** (2026-09-07 Des 指摘「Badge に
  // する必要ないもの」の実体がこれ)。design の 2 色に戻す。
  [TradingExecutionRoute.ENECHAIN]: 'info',
  [TradingExecutionRoute.ESQUARE_LIVE]: 'positive',
  [TradingExecutionRoute.MANUAL]: 'neutral',
  [TradingExecutionRoute.BACKUP_REGULAR]: 'neutral',
  [TradingExecutionRoute.BASELOAD_MARKET]: 'neutral',
  [TradingExecutionRoute.FORWARD_MARKET]: 'neutral',
  [TradingExecutionRoute.OTHER]: 'neutral',
}

const EXECUTION_ROUTE_LABEL_KEY: Record<number, string | undefined> = {
  [TradingExecutionRoute.ENECHAIN]:
    'page:supplyBilateral.executionRoute.enechain',
  [TradingExecutionRoute.ESQUARE_LIVE]:
    'page:supplyBilateral.executionRoute.eSquareLive',
  [TradingExecutionRoute.MANUAL]: 'page:supplyBilateral.executionRoute.manual',
  [TradingExecutionRoute.BACKUP_REGULAR]:
    'page:supplyBilateral.executionRoute.backupRegular',
  [TradingExecutionRoute.BASELOAD_MARKET]:
    'page:supplyBilateral.executionRoute.baseloadMarket',
  [TradingExecutionRoute.FORWARD_MARKET]:
    'page:supplyBilateral.executionRoute.forwardMarket',
  [TradingExecutionRoute.OTHER]: 'page:supplyBilateral.executionRoute.other',
}

/** 約定経路バッジ。 */
export const ExecutionRouteBadge = ({
  route,
}: ExecutionRouteBadgeProps): React.ReactElement | null => {
  const { t } = useTranslation()

  const status = EXECUTION_ROUTE_BADGE_STATUS[route]
  const labelKey = EXECUTION_ROUTE_LABEL_KEY[route]
  if (status === undefined || labelKey === undefined) return null

  return <Badge status={status}>{t(labelKey)}</Badge>
}

// -----------------------------------------------------------------------------
// DeliveryTypeBadge
// -----------------------------------------------------------------------------

type DeliveryTypeBadgeProps = {
  /** 受渡区分 (enum 固定値)。 旧 master row から enum に変更 (delivery-type-enum-spec §6)。 */
  deliveryType: TradingDeliveryType
}

/**
 * 受渡区分の種別 → semantic status。 色は現物 / 先物の 2 群だけで、 6 種の区別は
 * バッジの文字で行う (UI 辞書 screens/bilateral-trade.md 2e、 AC-SBL-004 Des 確定)。
 *
 *   - 現物 (bgRoute: BG・JEPX) — `neutral`
 *   - 先物 (financialSettlement: EEX・TOCOM・ICE・スワップ) — `info`。
 *     warning は重大度の語彙で「先物は警告」 と読めるため使わない
 *     (同 bilateral-trade.md の 2026-09-11 訂正)
 */
const DELIVERY_TYPE_BADGE_STATUS: Record<DeliveryTypeKind, BadgeStatus> = {
  bgRoute: 'neutral',
  financialSettlement: 'info',
}

/**
 * 受渡区分バッジ。 resolveDeliveryTypeKind / convertDeliveryTypeLabel で enum から
 * 導出する (= master fetch が不要な mock-app 準拠の proto enum 固定値)。
 */
export const DeliveryTypeBadge = ({
  deliveryType,
}: DeliveryTypeBadgeProps): React.ReactElement => (
  <Badge
    status={DELIVERY_TYPE_BADGE_STATUS[resolveDeliveryTypeKind(deliveryType)]}
  >
    {convertDeliveryTypeLabel(deliveryType)}
  </Badge>
)

// -----------------------------------------------------------------------------
// AreaCodeLabel / TimeZoneTypeLabel (= 単純な i18n 文字列化、 バッジではない)
// -----------------------------------------------------------------------------

const AREA_LABEL_KEY: Record<number, string | undefined> = {
  [AreaCode.HOKKAIDO]: 'hokkaido',
  [AreaCode.TOHOKU]: 'tohoku',
  [AreaCode.TOKYO]: 'tokyo',
  [AreaCode.CHUBU]: 'chubu',
  [AreaCode.HOKURIKU]: 'hokuriku',
  [AreaCode.KANSAI]: 'kansai',
  [AreaCode.CHUGOKU]: 'chugoku',
  [AreaCode.SHIKOKU]: 'shikoku',
  [AreaCode.KYUSHU]: 'kyushu',
  [AreaCode.OKINAWA]: 'okinawa',
}

/** エリアコードを日本語ラベルに変換。 */
export const AreaCodeLabel = ({
  areaCode,
}: {
  areaCode: AreaCode
}): React.ReactElement => {
  const { t } = useTranslation()
  const key = AREA_LABEL_KEY[areaCode]
  if (key === undefined) {
    return <span>—</span>
  }
  return <span>{t(`page:supplyBilateral.area.${key}`)}</span>
}

const TIME_ZONE_LABEL_KEY: Record<number, string | undefined> = {
  [TradingTimeZoneType.BASE]: 'base',
  [TradingTimeZoneType.PEAK]: 'peak',
  [TradingTimeZoneType.OFF_PEAK]: 'offPeak',
}

/** 時間帯区分を日本語ラベルに変換。 */
export const TimeZoneTypeLabel = ({
  timeZoneType,
}: {
  timeZoneType: TradingTimeZoneType
}): React.ReactElement => {
  const { t } = useTranslation()
  const key = TIME_ZONE_LABEL_KEY[timeZoneType]
  if (key === undefined) {
    return <span>—</span>
  }
  return <span>{t(`page:supplyBilateral.timeZoneType.${key}`)}</span>
}

// -----------------------------------------------------------------------------
// ProductTypeBadge — 商品形態 (標準 / スプレッド / 非定型)
// -----------------------------------------------------------------------------

/**
 * 商品形態 → semantic status。
 *
 * 商品形態は「状態」ではなく契約の種別だが、design にカテゴリカル専用クラスが無く
 * mock も汎用バッジで描いているため、design が semantic クラスをカテゴリカルに
 * 流用しているのに従う (= 標準 = neutral / スプレッド・非定型 = info の識別用途で、
 * status の意味では選んでいない)。
 */
const PRODUCT_TYPE_BADGE_STATUS: Record<number, BadgeStatus | undefined> = {
  [BilateralTradeProductType.STANDARD]: 'neutral',
  [BilateralTradeProductType.SPREAD]: 'info',
  [BilateralTradeProductType.NON_STANDARD]: 'info',
}

const PRODUCT_TYPE_LABEL_KEY: Record<number, string | undefined> = {
  [BilateralTradeProductType.STANDARD]:
    'page:supplyBilateral.productType.standard',
  [BilateralTradeProductType.SPREAD]: 'page:supplyBilateral.productType.spread',
  [BilateralTradeProductType.NON_STANDARD]:
    'page:supplyBilateral.productType.nonStandard',
}

/** 商品形態バッジ。UNSPECIFIED / 未知値は描画しない。 */
export const ProductTypeBadge = ({
  productType,
}: {
  productType: BilateralTradeProductType
}): React.ReactElement | null => {
  const { t } = useTranslation()
  const status = PRODUCT_TYPE_BADGE_STATUS[productType]
  const labelKey = PRODUCT_TYPE_LABEL_KEY[productType]
  if (status === undefined || labelKey === undefined) return null
  return <Badge status={status}>{t(labelKey)}</Badge>
}

const CALENDAR_KEY_LABEL_KEY: Record<number, string | undefined> = {
  [DeliveryCalendarKey.HOKKAIDO]: 'page:supplyBilateral.area.hokkaido',
  [DeliveryCalendarKey.TOHOKU]: 'page:supplyBilateral.area.tohoku',
  [DeliveryCalendarKey.TOKYO]: 'page:supplyBilateral.area.tokyo',
  [DeliveryCalendarKey.CHUBU]: 'page:supplyBilateral.area.chubu',
  [DeliveryCalendarKey.HOKURIKU]: 'page:supplyBilateral.area.hokuriku',
  [DeliveryCalendarKey.KANSAI]: 'page:supplyBilateral.area.kansai',
  [DeliveryCalendarKey.CHUGOKU]: 'page:supplyBilateral.area.chugoku',
  [DeliveryCalendarKey.SHIKOKU]: 'page:supplyBilateral.area.shikoku',
  [DeliveryCalendarKey.KYUSHU]: 'page:supplyBilateral.area.kyushu',
  [DeliveryCalendarKey.OKINAWA]: 'page:supplyBilateral.area.okinawa',
  [DeliveryCalendarKey.JEPX]: 'page:supplyBilateral.calendarKey.jepx',
  [DeliveryCalendarKey.TOCOM]: 'page:supplyBilateral.calendarKey.tocom',
  [DeliveryCalendarKey.EEX]: 'page:supplyBilateral.calendarKey.eex',
  [DeliveryCalendarKey.ICE]: 'page:supplyBilateral.calendarKey.ice',
}

/** 受給日カレンダー (14 値) を日本語ラベルに変換。 */
export const DeliveryCalendarKeyLabel = ({
  calendarKey,
}: {
  calendarKey: DeliveryCalendarKey
}): React.ReactElement => {
  const { t } = useTranslation()
  const key = CALENDAR_KEY_LABEL_KEY[calendarKey]
  if (key === undefined) {
    return <span>—</span>
  }
  return <span>{t(key)}</span>
}

const CUSTOM_DAY_CATEGORY_LABEL_KEY: Record<number, string | undefined> = {
  [TradingCustomDayCategory.WEEKDAY]:
    'page:supplyBilateral.customDayCategory.weekday',
  [TradingCustomDayCategory.HOLIDAY]:
    'page:supplyBilateral.customDayCategory.holiday',
  [TradingCustomDayCategory.ALL_DAYS]:
    'page:supplyBilateral.customDayCategory.allDays',
}

/**
 * 契約の時間帯区分ラベル。表示規則は spec 上は一覧・Recap・手入力取引閲覧
 * モーダルで共通 (spec bilateral-trade-list.md 表示データ「時間帯区分」、
 * issues #7762 / #7780 / #7782、2026-09-14 PdM 判断):
 *
 * 1. 名前付き (ベース / ピーク / オフピーク) → その名前
 * 2. それ以外の任意レンジ → 日区分＋時刻レンジ (先頭に「カスタム」は付けない)
 * 3. 時間帯の概念を持たない契約 (商品形態=非定型) → 「—」
 *
 * 2 をペア単位で出すのは、平日と休日で時刻レンジが違う非対称な契約 (旧オフ
 * ピーク相当) を日区分と時刻レンジの和集合で表示すると、実際より広い受渡に
 * 見えてしまうため (spec bilateral-trade.md §2「日区分・時間帯区分のペア
 * 対応」)。複数ペアの区切り ` / ` は前後に空白を置くので、一覧のセル
 * (`white-space: normal`) では折り返し位置になり、Recap・手入力取引閲覧
 * モーダルでは 1 行のまま同じ文字列になる。
 *
 * 3 に「-」(ハイフン) を使わないのは、時刻レンジ `8:00-20:00` と同じ字で
 * 欠けたレンジに見えるため (2026-09-14 判断)。
 *
 * **現在の配線は一覧 (TradeRow) と手入力取引閲覧モーダルの 2 経路だけ**。
 * Recap モーダルは `BilateralTradeRecap` に `custom_bands` が無く、proto 追加と
 * backend 配線が要るため本 component へ未接続 (#7934)。それまで Recap は
 * TimeZoneTypeLabel のまま = カスタム時間帯の契約が Recap でだけ「—」になる。
 */
export const LegTimeZoneLabel = ({
  timeZoneType,
  customBands,
}: {
  timeZoneType: TradingTimeZoneType
  customBands?: Array<{
    dayCategories: TradingCustomDayCategory[]
    timeSlots: Array<{ start: string; end: string }>
  }>
}): React.ReactElement => {
  const { t } = useTranslation()
  if (timeZoneType !== TradingTimeZoneType.UNSPECIFIED) {
    return <TimeZoneTypeLabel timeZoneType={timeZoneType} />
  }
  // 日区分は backend が 平日 / 休日 / 全日 の単一値で返す (畳み処理は backend の 1 箇所)。画面は受け取った値をそのままラベルへ引く。
  const bands = customBands ?? []
  const pairs = bands
    .map((band) => {
      const days = band.dayCategories
        .map((c) => CUSTOM_DAY_CATEGORY_LABEL_KEY[c])
        .filter((key): key is string => key !== undefined)
        .map((key) => t(key))
        .join('・')
      const slots = band.timeSlots
        .map((slot) => `${slot.start}-${slot.end}`)
        .join(', ')
      return [days, slots].filter((v) => v !== '').join(' ')
    })
    .filter((v) => v !== '')
  // enum も ペア列 も持たない契約 (商品形態=非定型) は時間帯の概念自体が無い
  // ので欠測記号を出す。「カスタム」(レンジ無し) にすると、カスタム時間帯の
  // 契約と一覧上で区別が付かなくなる。
  if (pairs.length === 0) {
    return <span>—</span>
  }
  return <span>{pairs.join(' / ')}</span>
}
